import React, { useRef } from 'react';
import { PostDocumentUploadResponse } from '@iwoca/lapi-client/edge';
import { Button, Icon } from '@iwoca/orion';
import { v4 } from 'uuid';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TUploadRejectReasonCode } from '../hooks/useFileUploadReducer';
import { fetchSaveUploadedDocumentsEdge } from '@api/lendingAPI/LendingAPI';
import { tracking } from '@iwoca/frontend-tracking-library';

type TProps = {
    buttonText?: string;
    classNames?: string;
    documentType?: PostDocumentUploadResponse['data']['document_type'];
    setFileLoading: (id: string, name: string) => void;
    setFileSuccess: (id: string) => void;
    setFileError: (id: string, errorReason: string) => void;
    setFileReject: (id: string, rejectReason: TUploadRejectReasonCode) => void;
};

function trackDocumentType(
    documentType: PostDocumentUploadResponse['data']['document_type']
) {
    switch (documentType) {
        case 'aged debtor report':
            return tracking.ampli.agedDebtorReportUploadClicked();
        case 'business plan':
            return tracking.ampli.businessPlanUploadClicked();
        case 'business accounts':
            return tracking.ampli.companyAccountsUploadClicked();
        case 'management accounts':
            return tracking.ampli.managementAccountsUploadClicked();
        case 'vat return':
            return tracking.ampli.vatReturnsUploadClicked();
    }
}

function trackDocumentTypeUploaded(
    documentType: PostDocumentUploadResponse['data']['document_type']
) {
    switch (documentType) {
        case 'aged debtor report':
            return tracking.ampli.agedDebtorReportUploaded();
        case 'business plan':
            return tracking.ampli.businessPlanUploaded();
        case 'business accounts':
            return tracking.ampli.companyAccountsUploaded();
        case 'management accounts':
            return tracking.ampli.managementAccountsUploaded();
        case 'vat return':
            return tracking.ampli.vatReturnsUploaded();
    }
}

const UploadButton = ({
    buttonText,
    classNames,
    documentType = 'other',
    setFileLoading,
    setFileError,
    setFileSuccess,
    setFileReject
}: TProps) => {
    const inputFileRef = useRef<HTMLInputElement>(null);
    const stateKey = useStateKey();

    const onButtonClick = () => {
        trackDocumentType(documentType);
        inputFileRef?.current?.click();
    };

    const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        // Prevents React from resetting its properties:
        e.persist();

        if (!e.target.files || !stateKey) {
            return;
        }

        const files = Array.from(e.target.files);

        await Promise.all(
            files.map(async (file) => {
                const uuid = v4();
                setFileLoading(uuid, file.name);
                const body = new FormData();

                body.append('document', file);
                body.append('document_type', documentType);

                // TODO: Update fetch call to use lapi-client post with form data fix
                const response = await fetchSaveUploadedDocumentsEdge({
                    impersonatingStateKey: stateKey,
                    body: body as FormData
                });

                if (response?.[0]?.errors?.[0]) {
                    setFileError(uuid, response?.[0].errors[0].detail);
                } else if (response?.[0].attributes?.status === 'rejected') {
                    setFileReject(
                        uuid,
                        response?.[0].attributes?.reject_reason
                    );
                } else {
                    setFileSuccess(uuid);
                    trackDocumentTypeUploaded(documentType);
                }
            })
        );

        // Reset input value to allow same file upload to fire onChange.
        e.target.value = '';
    };

    return (
        <>
            <Button
                variant="secondary"
                onClick={onButtonClick}
                className={classNames}
            >
                {buttonText ? buttonText : 'Upload'} <Icon icon="upload" />
            </Button>
            <input
                multiple
                hidden
                data-testid="upload-input"
                type="file"
                ref={inputFileRef}
                accept=".jpg, .jpeg, .png, .pdf"
                onChange={handleChange}
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default UploadButton;
